<template>
  <main class="flex flex-column">
    <Editor
      :api-key="key"
      :init="editorInit"
      v-model="editorRef"
      initial-value=""
      style="padding: 0px"
    />
  </main>
</template>
<script lang="ts" setup>
  import { onMounted, ref } from 'vue'
  import Editor from '@tinymce/tinymce-vue'
  import { storeToRefs } from 'pinia'
  import useCompany from '@/store/company'
  import { PageProperties } from '../services/dtos/ICreateLetterConfig'
  import { mergeTagsList } from '../utils/mergeTagsList'

  const key = ref('objx7vy5uv46a1rzrwmtjldk46hh2306k5n8hq42j4zj3afs')
  const editorRef = ref()
  const props = defineProps<{
    template?: string
    pageProperties: PageProperties
  }>()

  const { company } = storeToRefs(useCompany())

  const defaultInch = 0.39 * 2.34 * 2

  const margins = ref({
    top: defaultInch,
    right: defaultInch,
    bottom: defaultInch,
    left: defaultInch,
  })

  onMounted(() => {
    if (props.template) editorRef.value = props.template
    else editorRef.value = ''

    if (props.pageProperties.margins) {
      const { top, right, bottom, left } = props.pageProperties.margins

      margins.value = {
        top: Math.round(top * 2.54), //inches to cm
        right: Math.round(right * 2.54),
        bottom: Math.round(bottom * 2.54),
        left: Math.round(left * 2.54),
      }
    }
  })

  const mergetags_list = ref<any[]>(mergeTagsList)

  const flMenu = mergetags_list.value
    .filter((item) =>
      item.notInclude
        ? !item.notInclude.includes(company.value?.client.code)
        : true
    )
    .map((item: any) => {
      // If the item has a menu array, filter it; otherwise, return the item as is.
      if (Array.isArray(item.menu)) {
        return {
          ...item,
          menu: item.menu.filter((subItem: any) =>
            subItem.notInclude
              ? !subItem.notInclude.includes(company.value?.client.code)
              : true
          ),
        }
      }
      return item
    })

  const filePickerCallBack = (callback: any, value: any, meta: any) => {
    let input: any = document.createElement('input')
    input.setAttribute('type', 'file')
    input.setAttribute('accept', 'image/*')
    // Listen for file selection
    input.onchange = function () {
      let file = input.files[0]
      let reader = new FileReader()

      // When the file is read as base64
      reader.onload = function (e: any) {
        // Get the base64 string
        let base64 = e.target.result

        // Use the callback to insert the base64 image into the editor
        callback(base64, {
          alt: file.name, // Set the alt attribute to the file name
        })
      }

      // Read the file as a base64 string
      reader.readAsDataURL(file)
    }

    // Trigger the file input click event
    input.click()
  }

  const base64str = (blobInfo: any) => {
    const base64str =
      'data:' + blobInfo.blob().type + ';base64,' + blobInfo.base64()

    return Promise.resolve(base64str)
  }

  const onEditor = (editor: any) => {
    // Evento para inicializar el editor y configurar los márgenes
    editor.on('init', function (e: any) {
      // Establecer el contenido inicial
      editor.setContent(props.template || '')

      const contentBody = editor.getDoc().querySelector('.mce-content-body')
      contentBody.style.paddingTop = `${margins.value.top}cm`
      contentBody.style.paddingRight = `${margins.value.right}cm`
      contentBody.style.paddingBottom = `${margins.value.bottom}cm`
      contentBody.style.paddingLeft = `${margins.value.left}cm`
    })

    editor.on('SaveContent', function (e: any) {
      editor.setContent(editor.getContent())
      editorRef.value = editor.getContent()
    })
    // Botón separado para guardar (reemplaza el antiguo 'Condicionales')
    editor.ui.registry.addButton('saveTemplate', {
      text: 'Guardar',
      icon: 'save',
      tooltip: 'Guardar plantilla',
      onAction: () => editor.execCommand('mceSave'),
    })

    // Helper para insertar estructura condicional
    const insertConditional = (
      condition: string,
      tBranch: string,
      fBranch: string
    ) => {
      const c = condition.trim() || 'true'
      const t = tBranch.trim() || 'TEXTO_SI_VERDADERO'
      const f = fBranch.trim() || 'TEXTO_SI_FALSO'
      editor.insertContent(`<< ${c} ? ${t} : ${f} >>`)
    }

    // Menú para crear / insertar condicionales
    editor.ui.registry.addMenuButton('Condicionales', {
      text: 'Condicional',
      tooltip: 'Insertar condicional',
      fetch: (callback: any) => {
        callback([
          {
            type: 'menuitem',
            text: 'Estructura vacía',
            onAction: () =>
              insertConditional(
                'condicion',
                'TEXTO_SI_VERDADERO',
                'TEXTO_SI_FALSO'
              ),
          },
        ])
      },
    })

    editor.ui.registry.addButton('marginSettings', {
      text: 'Márgenes',
      icon: 'settings',
      onAction: function () {
        editor.windowManager.open({
          title: 'Márgenes',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'top',
                label: 'Margen Superior (cm)',
                inputMode: 'numeric',
                min: 0,
                max: 5,
              },
              {
                type: 'input',
                name: 'right',
                label: 'Margen Derecho (cm)',
                inputMode: 'numeric',
                min: 0,
                max: 5,
              },
              {
                type: 'input',
                name: 'bottom',
                label: 'Margen Inferior (cm)',
                inputMode: 'numeric',
                min: 0,
                max: 5,
              },
              {
                type: 'input',
                name: 'left',
                label: 'Margen Izquierdo (cm)',
                inputMode: 'numeric',
                min: 0,
                max: 5,
              },
            ],
          },
          initialData: {
            top: String(margins.value.top),
            right: String(margins.value.right),
            bottom: String(margins.value.bottom),
            left: String(margins.value.left),
          },
          buttons: [
            { type: 'cancel', text: 'Cancel' },
            {
              type: 'submit',
              text: 'Apply',
              primary: true,
            },
          ],
          onSubmit: function (dialog: any) {
            const data = dialog.getData()
            const minValue = 0
            const maxValue = 5

            // Validate each field
            let isValid = true
            ;['top', 'right', 'bottom', 'left'].forEach((field) => {
              const value = parseFloat(data[field])
              if (isNaN(value) || value < minValue || value > maxValue) {
                editor.windowManager.alert(
                  `Favor ingresar un numero entre ${minValue} y ${maxValue} para el  ${field} margin.`
                )
                isValid = false
              }
            })

            if (isValid) {
              const contentBody = editor
                .getDoc()
                .querySelector('.mce-content-body')
              // Apply new styles
              if (contentBody) {
                contentBody.style.paddingTop = `${data.top}cm`
                contentBody.style.paddingRight = `${data.right}cm`
                contentBody.style.paddingBottom = `${data.bottom}cm`
                contentBody.style.paddingLeft = `${data.left}cm`

                // Update margins
                margins.value = {
                  top: data.top,
                  right: data.right,
                  bottom: data.bottom,
                  left: data.left,
                }
              }
              dialog.close()
            }
          },
        })
      },
    })

    const mergeTagValue = 'lastPayroll.concept{{code}}.amount'
    editor.on('click', function (e: any) {
      let selectedContent = editor.selection
        .getContent({ format: 'text' })
        .trim()
      // Check if the selected content matches the merge tag value
      if (selectedContent.indexOf(mergeTagValue) !== -1) {
        editor.windowManager.open({
          title: 'Insertar código de concepto',
          body: {
            type: 'panel',
            items: [
              {
                type: 'input',
                name: 'code',
                label: 'Código del concepto',
                placeholder: 'Ejemplo: 001',
              },
            ],
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancel',
            },
            {
              type: 'submit',
              text: 'Insert',
              primary: true,
            },
          ],
          onSubmit: function (dialog: any) {
            // Get the value entered by the user
            const data = dialog.getData()
            const code = data.code.trim()

            // If a code was entered, insert it into the editor
            if (code) {
              // Replace {{code}} with the user's input
              let updatedTag = selectedContent.replace('{{code}}', code)
              // Replace the selected content with the updated tag
              editor.selection.setContent(updatedTag)

              dialog.close()
            }
          },
        })
      }
    })

    editor.on('dblclick', function (e: any) {
      // Check if the double-clicked element is an image
      const targetElement = e.target
      if (targetElement && targetElement.nodeName === 'IMG') {
        // Prompt the user to enter a CSS class
        editor.windowManager.open({
          title: 'Agregar estilo a la imagen',
          body: {
            type: 'panel',
            items: [
              {
                type: 'selectbox',
                name: 'cssClass',
                label: 'Clase de estilo',
                placeholder: 'Ejemplo: watermark',
                items: [
                  { text: 'Marca de agua', value: 'watermark' },
                  { text: 'Ancho completo', value: 'fullWidth' },
                  { text: 'Borrar estilo', value: 'erase' },
                  { text: 'Pie de página', value: 'footer' },
                ],
              },
            ],
          },
          buttons: [
            {
              type: 'cancel',
              text: 'Cancel',
            },
            {
              type: 'submit',
              text: 'Apply',
              primary: true,
            },
          ],
          onSubmit: function (dialog: any) {
            // Get the value entered by the user
            const data = dialog.getData()
            const cssClass = data.cssClass.trim()

            // If a CSS class was entered, apply it to the image
            if (cssClass) {
              switch (cssClass) {
                case 'watermark':
                  //delete previous styles
                  targetElement.style = ''
                  targetElement.style =
                    'position: fixed; top: 50%; left: 50%; transform: translate(-50%, -50%); opacity: 0.15;' // Set the CSS class for the image
                  break
                case 'erase':
                  targetElement.style = ''
                  targetElement.style = '' // Set the CSS class for the image
                  break
                case 'fullWidth':
                  targetElement.style = ''
                  targetElement.style = 'width: 100%;' // Set the CSS class for the image
                  break
                case 'footer':
                  targetElement.style = ''
                  targetElement.style =
                    'position: fixed; bottom: 0; width: 100%; text-align: center; font-size: smaller; opacity: 0.7;' // Set the CSS class for the image
                  break
              }
            }

            dialog.close()
          },
        })
      }
    })
  }

  const editorInit = ref({
    toolbar_mode: 'sliding',
    plugins: [
      'advcode',
      'advlist',
      'advtable',
      'advtemplate',
      'anchor',
      'autolink',
      'autosave',
      'casechange',
      'charmap',
      'checklist',
      'codesample',
      'directionality',
      'editimage',
      'emoticons',
      'export',
      'footnotes',
      'formatpainter',
      'help',
      'image',
      'insertdatetime',
      'link',
      'linkchecker',
      'lists',
      'media',
      'mediaembed',
      'mergetags',
      'nonbreaking',
      'pagebreak',
      'permanentpen',
      'searchreplace',
      'table',
      'tableofcontents',
      'visualblocks',
      'visualchars',
      'wordcount, inlinecss',
    ],
    toolbar:
      'marginSettings Condicionales | undo redo | blocks  fontsize  | bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | mergetags | outdent indent | bullist numlist | link image | forecolor backcolor | removeformat | code fullscreen preview',
    editable_root: true,
    height: '80vh',
    file_picker_types: 'image',
    images_file_types: 'jpg,svg,webp,jpeg,png',
    file_picker_callback: filePickerCallBack,
    images_upload_handler: base64str,
    images_upload_url: false,
    image_uploadtab: true,
    mergetags_list: flMenu,
    content_css: 'document',
    language: 'es',
    setup: onEditor,
    advtemplate_templates: [
      {
        title: 'Tabla de asignaciones',
        description:
          'Tabla con el detalle de todas las asignaciones del ultimo periodo de nómina',
        content: `
               <p style="line-height: 0; text-align: left;"><span style="font-family: arial, helvetica, sans-serif;">&lt;&lt; for lastPayroll.earningsDetails &gt;&gt;</span></p>
                  <table style="border-collapse: collapse; width: 56.9661%; height: 38px; border-width: 1px; border-color: rgb(0, 0, 0); margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 51.7123%;"><col style="width: 48.2877%;"></colgroup>
                  <tbody>
                  <tr>
                  <td style="text-align: left; border-color: rgb(0, 0, 0);"><span style="color: rgb(0, 0, 0); font-size: 10pt;"><strong>Concepto</strong></span></td>
                  <td style="border-color: rgb(0, 0, 0); text-align: right;"><span style="font-size: 10pt;"><strong>Monto</strong></span></td>
                  </tr>
                  <tr style="height: 21px;">
                  <td style="height: 21px; text-align: left; border-color: rgb(0, 0, 0);"><span style="color: rgb(0, 0, 0); font-size: 10pt;">{{item.conceptName}}</span></td>
                  <td style="height: 21px; border-color: rgb(0, 0, 0); text-align: right;"><span style="color: rgb(0, 0, 0); font-size: 10pt;">{{item.amount|0.0,00}}</span></td>
                  </tr>
                  <tr style="height: 17px;">
                  <td style="border-color: rgb(0, 0, 0); height: 17px; text-align: left;"><span style="color: rgb(0, 0, 0); font-size: 10pt;"><strong>Total de asignaciones</strong></span></td>
                  <td style="border-color: rgb(0, 0, 0); height: 17px; text-align: right;"><span style="color: rgb(0, 0, 0); font-size: 10pt;">{{lastPayroll.concept9999-1.amount|0.0,00}}</span></td>
                  </tr>
                  </tbody>
                  </table>
            <p style="text-align: left;">&lt;&lt;/for&gt;&gt;</p>
        `,
      },
      {
        title: 'Tabla de deducciones',
        description:
          'Tabla con el detalle de todas las deducciones del ultimo periodo de nómina',
        content: `
              <p style="line-height: 0; text-align: left;"><span style="font-family: arial, helvetica, sans-serif;">&lt;&lt; for lastPayroll.deductionsDetails&gt;&gt;</span></p>
                <table style="border-collapse: collapse; width: 56.9661%; height: 38px; border-width: 1px; border-color: rgb(0, 0, 0); margin-left: 0px; margin-right: auto;" border="1"><colgroup><col style="width: 51.7123%;"><col style="width: 48.2877%;"></colgroup>
                <tbody>
                <tr>
                <td style="text-align: left; border-color: rgb(0, 0, 0);"><span style="color: rgb(0, 0, 0); font-size: 10pt;"><strong>Concepto</strong></span></td>
                <td style="border-color: rgb(0, 0, 0); text-align: right;"><span style="font-size: 10pt;"><strong>Monto</strong></span></td>
                </tr>
                <tr style="height: 21px;">
                <td style="height: 21px; text-align: left; border-color: rgb(0, 0, 0);"><span style="color: rgb(0, 0, 0); font-size: 10pt;">{{item.conceptName}}</span></td>
                <td style="height: 21px; border-color: rgb(0, 0, 0); text-align: right;"><span style="color: rgb(0, 0, 0); font-size: 10pt;">{{item.amount|0.0,00}}</span></td>
                </tr>
                <tr style="height: 17px;">
                <td style="border-color: rgb(0, 0, 0); height: 17px; text-align: left;"><span style="color: rgb(0, 0, 0); font-size: 10pt;"><strong>Total de deducciones</strong></span></td>
                <td style="border-color: rgb(0, 0, 0); height: 17px; text-align: right;"><span style="color: rgb(0, 0, 0); font-size: 10pt;">{{lastPayroll.concept29999-1.amount|0.0,00}}</span></td>
                </tr>
                </tbody>
                </table>
              <p style="text-align: left;">&lt;&lt;/for&gt;&gt;</p>
        `,
      },
    ],
    content_style: ` 
    `,
  })
  defineExpose({
    editorRef,
    margins,
  })
</script>
<style scoped>
  /* Header and footer styles */
  #footer-editor,
  #header-editor {
    position: fixed;
    width: 100%;
    text-align: center;
    background: #f8f9fa;
    padding: 5px;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
    font-size: smaller;
  }

  #footer-editor {
    bottom: 0;
  }

  #header-editor {
    top: 0;
  }

  /* Add margin to editor to make space for footer and header */
  .tox-editor-container {
    margin-top: 40px;
    /* Adjust this value as needed */
    margin-bottom: 40px;
    /* Adjust this value as needed */
  }
</style>
